import {
  getApiV1ThreeLetterCodePartnerAdminsApplicationConfiguration,
  getGetApiV1ThreeLetterCodePartnerAdminsApplicationConfigurationKey,
} from '@fukuri-admins/apis/client/gen/endpoints';
import type { GetApiV1ThreeLetterCodePartnerAdminsApplicationConfiguration200 } from '@fukuri-admins/apis/client/gen/model';
import { useFetch } from '@fukuri-admins/hooks/common';
import { isAxiosError } from 'axios';

type Args = {
  /** 3文字コード。未ログイン時は falsy になり得る */
  threeLetterCode: string;
  /** 401（認証エラー）検知時に呼び出すコールバック */
  onUnauthorized?: () => void;
};

type ReturnType = {
  /** アプリケーション設定データ（取得完了前は undefined） */
  data: GetApiV1ThreeLetterCodePartnerAdminsApplicationConfiguration200 | undefined;
  /** 取得中かどうか */
  isLoading: boolean;
  /** 取得エラー（発生していない場合は undefined） */
  error: unknown;
};

/**
 * useGetPartnerAdminsApplicationConfigurationフックの型定義
 */
type IUseGetPartnerAdminsApplicationConfiguration = (args: Args) => ReturnType;

/**
 * パートナー管理者向けアプリケーション設定取得フック
 *
 * ConfigurationProvider はアプリシェル起動時（認証確認前後の境界）にこの設定を取得するため、
 * 上位に Suspense 境界を前提にできない。そのため `suspense: false` で呼び出し、
 * data / isLoading / error を呼び出し元（ConfigurationProvider）に返して
 * ts-pattern の match で表示を分岐させる方針にしている。
 *
 * threeLetterCode が falsy（未ログイン等）のときは SWR キーを null にして
 * fetch 自体をスキップする（未ログイン時の 401 誘発を防ぐ、条件付きフェッチ）。
 *
 * `suspense: false` のため SWR の `onError` が正しく機能する（suspense モードのような
 * エラーのすり抜け・エスカレーションが発生しない）。401 検知時のみ `onUnauthorized` を
 * 発火させ、呼び出し元（ConfigurationProvider）が signOut 等の後続処理を行えるようにする。
 * HTTPステータスの解釈・分岐は呼び出し側の責務のため、この hook は `error` をそのまま返す
 * （status 等に加工しない）。
 */
export const useGetPartnerAdminsApplicationConfiguration: IUseGetPartnerAdminsApplicationConfiguration =
  ({ threeLetterCode, onUnauthorized }) => {
    const { data, isLoading, error } =
      useFetch<GetApiV1ThreeLetterCodePartnerAdminsApplicationConfiguration200>(
        threeLetterCode
          ? getGetApiV1ThreeLetterCodePartnerAdminsApplicationConfigurationKey(threeLetterCode)
          : null,
        () => getApiV1ThreeLetterCodePartnerAdminsApplicationConfiguration(threeLetterCode),
        {
          suspense: false,
          onError: err => {
            if (isAxiosError(err) && err.response?.status === 401) {
              onUnauthorized?.();
            }
          },
        }
      );

    return { data, isLoading, error };
  };
